import { Box, Button } from "@mui/material";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import type { Report } from "../../../../../api/types/svTypes";
import { useEnhancedBiApiClientProvider } from "../../../reporting/sv/contexts/ApiClientProviderContext";
import useCurrentReportConfiguration from "../../../reporting/sv/hooks/useCurrentReportConfiguration";
import type { AppDispatch } from "../../../reporting/sv/store/store";
import { setEditReport } from "../../../reporting/sv/store/thunks/currentReportThunks";

interface Props {
  report: Report;
  onSaved: () => void;
}

export const SaveButton = ({ report, onSaved }: Props) => {
  const { updateReport } = useEnhancedBiApiClientProvider();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const dispatch: AppDispatch = useDispatch();
  const [saving, setSaving] = useState(false);

  const { getCurrentReportConfigurationAsync } = useCurrentReportConfiguration();

  const saveExistedReport = useCallback(async () => {
    const reportConfiguration = await getCurrentReportConfigurationAsync();

    if (reportConfiguration === undefined) {
      return;
    }

    setSaving(true);
    const rep = { ...report };
    rep.configuration = reportConfiguration;

    updateReport(rep)
      .then((response) => {
        if (response.success && response.data !== undefined) {
          dispatch(setEditReport(response.data));
          sendNotification("The report was successfully saved.");
          onSaved();
        } else {
          sendNotificationError("An error occurred while saving the report.");
        }
      })
      .catch(() => {
        sendNotificationError("An error occurred while saving the report.");
      })
      .finally(() => setSaving(false));
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [getCurrentReportConfigurationAsync, report, sendNotification, sendNotificationError, updateReport]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexGrow: 0,
          alignItems: "center",
          gap: "1rem",
        }}
      >
        <Button variant="contained" color="primary" onClick={saveExistedReport} disabled={saving} loading={saving}>
          Save
        </Button>
      </Box>
    </>
  );
};

export default SaveButton;
